import { useCallback, useEffect, useState } from 'react';
import { participationAPI, usersAPI } from '../services/api';

/**
 * Tracks the current user's participation status (the "who may participate" gate)
 * and exposes the actions needed to clear it.
 *
 * state mirrors the server: 'permitted' | 'blocked_pending_age' |
 * 'blocked_pending_guardian' | 'guardian_requested' | 'denied' | 'revoked'.
 * The server re-derives state from the profile on every read, so submitting a
 * birth year or recording a guardian grant is reflected on the next refresh.
 */
export function useParticipation() {
  const [state, setState] = useState('loading');
  const [reason, setReason] = useState(null);

  const refresh = useCallback(async () => {
    setState('loading');
    try {
      const { data } = await participationAPI.getStatus();
      setState(data.state);
      setReason(data.reason);
    } catch (e) {
      setState('error');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Provide the birth year (clears blocked_pending_age; may reveal minor status).
  const submitBirthYear = useCallback(async (birthYear) => {
    await usersAPI.updateProfile({ birth_year: birthYear });
    await refresh();
  }, [refresh]);

  const requestGuardian = useCallback(async ({ name, email, relationship }) => {
    await participationAPI.requestGuardian({
      guardian_name: name, guardian_email: email, guardian_relationship: relationship,
    });
    await refresh();
  }, [refresh]);

  // Opt back in after withdrawing (adult → permitted; minor → guardian gate again).
  const rejoin = useCallback(async () => {
    await participationAPI.rejoin();
    await refresh();
  }, [refresh]);

  return { state, reason, refresh, submitBirthYear, requestGuardian, rejoin };
}
